import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	deleteTasksBulk,
	taskItemsRestCrudThunksForBuyer,
	taskItemsRestCrudThunksForSupplier,
	updateTasksBulk,
} from '../../thunks/task_items_thunks';
import { message, Spin, Switch } from 'antd';
import { isAllowedToEditTasksByWOStatus } from '../../utils/WorkOrderUtils';
import { isUserAllowedToAccess, PERMISSION_NAMES } from '../../utils/AuthUtils';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { LoadingOutlined } from '@ant-design/icons';
import {
	workOrdersRestCrudThunksForBuyer,
	workOrdersRestCrudThunksForSupplier,
} from '../../thunks/work_orders_thunks';
import WOTaskSectionView from './WOTaskSectionView';

const WOTasksSection: FC<any> = ({
	workOrder,

	currentUser,
	companyConfig,
	userType,
	fetchTaskItems,
	updateTasks,
	deleteTasks,
	updateWorkOrderPartially,
	updateTask,
	createTask,
	deleteTask,
}): React.ReactElement => {
	const [loading, setLoading] = useState(false);
	const [updatingWO, setUpdatingWO] = useState(false);
	const [existingTasks, setExistingTasks] = useState<any[]>([]);
	const [taskCompletionMandatory, setTaskCompletionMandatory] = useState(
		nullSafeGetOrElse('taskCompletionMandatory', workOrder, false)
	);

	const canEdit = useMemo(
		() => isAllowedToEditTasksByWOStatus(workOrder, currentUser),
		[currentUser, workOrder]
	);

	const workOrderId = useMemo(() => workOrder.id, [workOrder.id]);

	const canEditByAccessPermission = useMemo(
		() =>
			isUserAllowedToAccess(userType)(
				PERMISSION_NAMES.MODIFY_WO_TASKS,
				companyConfig,
				currentUser.roles
			),
		[companyConfig, currentUser.roles, userType]
	);

	const isAllowedToEdit = useMemo(
		() => canEdit && canEditByAccessPermission,
		[canEdit, canEditByAccessPermission]
	);

	useEffect(() => {
		setLoading(true);
		fetchTaskItems({ workOrderId }, 'taskItemsIndex', { current: 1, pageSize: 500 })
			.then((tasks) => {
				setExistingTasks(
					tasks.map((task) => ({
						...task,
						groupTitle: nullSafeGetOrElse('groupTitle', task, ''),
					}))
				);
			})
			.catch(message.error)
			.finally(() => setLoading(false));
	}, [workOrderId]);

	const onTaskCompletionChange = useCallback(
		(val) => {
			if (updatingWO) {
				return;
			}
			setTaskCompletionMandatory(val);
			setUpdatingWO(true);
			updateWorkOrderPartially(workOrderId, {
				taskCompletionMandatory: val,
			})
				.catch(message.error)
				.finally(() => setUpdatingWO(false));
		},
		[updateWorkOrderPartially, updatingWO, workOrderId]
	);

	const bulkUpdateTasks = useCallback(
		(toBeUpdatedTasks) => {
			return new Promise((resolve, reject) => {
				nullSafeGet('0', toBeUpdatedTasks)
					? updateTasks(toBeUpdatedTasks).then(resolve).catch(reject)
					: resolve(true);
			});
		},
		[updateTasks]
	);

	const bulkDeleteTasks = useCallback(
		(toBeDeletedTasks) => {
			return new Promise((resolve, reject) => {
				nullSafeGet('0', toBeDeletedTasks)
					? deleteTasks(toBeDeletedTasks).then(resolve).catch(reject)
					: resolve(true);
			});
		},
		[deleteTasks]
	);

	const onTaskEdit = useCallback(
		(task) => {
			const saveAction = !!task.id ? updateTask : createTask;
			const updatedTask = {
				...task,
				workOrderId: parseInt(workOrderId),
			};
			return saveAction(updatedTask);
		},
		[createTask, updateTask, workOrderId]
	);

	const onTaskDelete = useCallback(
		(task) => {
			if (!task.id) {
				return new Promise((resolve) => resolve(task));
			}

			return deleteTask(task);
		},
		[deleteTask]
	);

	return (
		<WOTaskSectionView
			tasks={existingTasks}
			loading={loading}
			extra={
				<div className="flex flex-row items-center">
					{updatingWO ? (
						<div className="mr-2">
							<Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
						</div>
					) : null}
					<Switch
						disabled={!isAllowedToEdit}
						checked={taskCompletionMandatory}
						onChange={onTaskCompletionChange}
					/>
					<label className="ml-2">All Tasks Required</label>
				</div>
			}
			workOrder={workOrder}
			isAllowedToEdit={isAllowedToEdit}
			bulkUpdateTasks={bulkUpdateTasks}
			bulkDeleteTasks={bulkDeleteTasks}
			onTaskEdit={onTaskEdit}
			onTaskDelete={onTaskDelete}
			setExistingTasks={setExistingTasks}
		/>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchTaskItems: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? taskItemsRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: taskItemsRestCrudThunksForBuyer.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	updateWorkOrderPartially: (id, entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrdersRestCrudThunksForSupplier.updatePartially(id, entity)
				: workOrdersRestCrudThunksForBuyer.updatePartially(id, entity)
		),
	updateTasks: (entities) => dispatch(updateTasksBulk(ownProps.userType)(entities)),
	deleteTasks: (entities) => dispatch(deleteTasksBulk(ownProps.userType)(entities)),
	createTask: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? taskItemsRestCrudThunksForSupplier.create(entity)
				: taskItemsRestCrudThunksForBuyer.create(entity)
		),
	updateTask: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? taskItemsRestCrudThunksForSupplier.update(entity)
				: taskItemsRestCrudThunksForBuyer.update(entity)
		),
	deleteTask: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? taskItemsRestCrudThunksForSupplier.delete(entity, 'id', null, false, true)
				: taskItemsRestCrudThunksForBuyer.delete(entity, 'id', null, false, true)
		),
});

const ComponentWithoutUserType = connect(mapStateToProps, mapDispatchToProps)(WOTasksSection);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
